// Offers recordings that were not saved (crashed tab, dead battery, upload refused by the server)
// one at a time: save into their document, download, or discard.
import { useCallback, useEffect, useState } from 'react';
import type { CreateDocumentRequest, DocType } from '../../../shared/types';
import { addAudioClip, createDocument, getDocument } from '../lib/api';
import { DOC_TYPE_LABEL, formatDateTime, formatDuration } from '../lib/format';
import { useOutboxStatus } from '../lib/hooks';
import { recordingsInUse } from '../lib/localdb';
import { extensionForMime } from '../lib/outbox';
import { discardRecording, listRecoverableRecordings, recoverRecording, type RecoverableRecording } from '../lib/recorder';
import { navigate } from '../lib/router';
import { actingUserId } from '../lib/session';
import { saveFile } from '../export';
import { confirmDialog, Modal } from './Dialog';
import { errorText, formatBytes, isAbortError, isDocType, isUuid, newId, routeForDocument, warnNotPersisted } from './helpers';
import { Icon } from './Icon';
import { toast } from './Toasts';

/**
 * Without Web Locks, records written to within this window may belong to a recording running in
 * another tab (its recorder refreshes the record every few seconds).
 */
const ACTIVE_ELSEWHERE_MS = 20_000;
/** Extra wait after a skipped record's window ends before it is checked again. */
const RESCAN_MARGIN_MS = 1_000;

/** Postponed with "Pozneje": not offered again in this app session (survives re-mounts, e.g. a re-login). */
const postponed = new Set<string>();

/**
 * True when other tabs' recordings are visible as Web Locks (localdb.recordingsInUse): then a
 * record nobody holds is offered at once (e.g. right after a crash), not only after a delay.
 */
async function webLocksWork(): Promise<boolean> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (typeof locks?.query !== 'function') return false;
  try {
    await locks.query();
    return true;
  } catch {
    return false;
  }
}

interface Target {
  docId: string;
  /** `docId` was generated here (the recording named no document). */
  isNew: boolean;
  type: DocType;
  create: CreateDocumentRequest;
  clipId: string;
  seq?: number;
  diarize: boolean;
  speakerCount?: number;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);
const int = (v: unknown): number | undefined => (typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : undefined);

/** Where a recovered recording goes. Document creation is idempotent, so re-using ids is safe. */
async function resolveTarget(r: RecoverableRecording, meta: Record<string, unknown>): Promise<Target> {
  const rejected = meta.rejected === true;
  const docId = isUuid(meta.docId) ? meta.docId : null;
  let type: DocType | null = isDocType(meta.type) ? meta.type : null;

  if (docId && !type) {
    // Uploads rescued from the outbox only know their document id: ask for its type.
    try {
      const existing = await getDocument(docId);
      if (existing) type = existing.type;
    } catch {
      // Offline and not cached: fall back to the recording profile below.
    }
  }
  const resolvedType: DocType = type ?? (r.profile === 'meeting' ? 'meeting' : 'note');
  const title = str(meta.title);
  const create: CreateDocumentRequest = { type: resolvedType, createdAt: r.startedAt };
  if (title && resolvedType !== 'note') create.title = title;

  const target: Target = {
    docId: docId ?? newId(),
    isNew: docId === null,
    type: resolvedType,
    create,
    // A refused upload keeps a fresh id, so the new attempt is not mistaken for the refused one.
    clipId: !rejected && isUuid(meta.clipId) ? meta.clipId : newId(),
    diarize: typeof meta.diarize === 'boolean' ? meta.diarize : resolvedType === 'meeting',
  };
  const seq = rejected ? undefined : int(meta.seq);
  if (seq !== undefined) target.seq = seq;
  const speakerCount = int(meta.speakerCount);
  if (speakerCount !== undefined) target.speakerCount = speakerCount;
  return target;
}

function describe(r: RecoverableRecording): string {
  const type = isDocType(r.meta.type) ? DOC_TYPE_LABEL[r.meta.type] : r.profile === 'meeting' ? 'Sestanek' : 'Glasovni zapis';
  const title = str(r.meta.title);
  return title ? `${type} »${title}«` : type;
}

export function RecoveryManager() {
  const [items, setItems] = useState<RecoverableRecording[]>([]);
  const [busy, setBusy] = useState<'save' | 'download' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Changes with every refusal: the list is newest first and capped, so its length alone stops
  // changing once it is full.
  const failed = useOutboxStatus().failed;
  const failedKey = `${failed.length}|${failed[0]?.at ?? ''}|${failed[0]?.kind ?? ''}|${failed[0]?.message ?? ''}`;

  /** Updates the offered list; returns when to look again (ms), or null. */
  const scan = useCallback(async (): Promise<number | null> => {
    let list: RecoverableRecording[];
    let inUse: Set<string>;
    let locked: boolean;
    try {
      [list, inUse, locked] = await Promise.all([
        listRecoverableRecordings(),
        recordingsInUse().catch(() => new Set<string>()),
        webLocksWork(),
      ]);
    } catch (err) {
      console.warn('[recovery] listing failed', err);
      return null;
    }
    const now = Date.now();
    const userId = actingUserId();
    let next: number | null = null;
    const offer = list.filter((r) => {
      // Held by a recorder or by a queued upload, in this tab or (Web Locks) another one.
      if (postponed.has(r.id) || inUse.has(r.id)) return false;
      // Recorded by someone else (e.g. in another tab before a sign-in here): never offered to this user.
      if (typeof r.meta.userId === 'string' && r.meta.userId !== userId) return false;
      const age = now - new Date(r.updatedAt).getTime();
      if (!locked && age < ACTIVE_ELSEWHERE_MS) {
        // Maybe still being written in another tab: look again as soon as its window has passed.
        const wait = ACTIVE_ELSEWHERE_MS - age + RESCAN_MARGIN_MS;
        next = next === null ? wait : Math.min(next, wait);
        return false;
      }
      return true;
    });
    setItems(offer);
    return next;
  }, []);

  // On start, and whenever the outbox had to refuse something (it rescues refused uploads).
  useEffect(() => {
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      void scan().then((wait) => {
        if (!cancelled && wait !== null) retry = setTimeout(run, wait);
      });
    };
    run();
    return () => {
      cancelled = true;
      if (retry !== null) clearTimeout(retry);
    };
  }, [scan, failedKey]);

  const current = items[0];
  if (!current) return null;

  const done = (id: string) => {
    setItems((list) => list.filter((r) => r.id !== id));
    setError(null);
  };

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      const recovered = await recoverRecording(current.id);
      const target = await resolveTarget(current, recovered.meta);
      // The document usually exists already (with entries, header fields, ...): only one created
      // here may be shown without asking the server (see api.createDocument).
      await createDocument(target.docId, target.create, { isNew: target.isNew });
      const { persisted } = await addAudioClip(target.docId, {
        id: target.clipId,
        blob: recovered.blob,
        mimeType: recovered.mimeType,
        durationMs: recovered.durationMs,
        createdAt: recovered.startedAt,
        ...(target.seq !== undefined ? { seq: target.seq } : {}),
        diarize: target.diarize,
        ...(target.speakerCount !== undefined ? { speakerCount: target.speakerCount } : {}),
      });
      // Only now is the audio safe in the outbox. When it is only in memory, this copy stays (held
      // by the queued upload) until the server has the audio.
      if (persisted) await discardRecording(current.id).catch((err: unknown) => console.warn('[recovery] discard failed', err));
      done(current.id);
      if (persisted) toast('Posnetek je shranjen in bo prepisan.', { kind: 'success' });
      else warnNotPersisted('recoverable');
      navigate(routeForDocument({ id: target.docId, type: target.type }));
    } catch (err) {
      setError(errorText(err, 'Posnetka ni bilo mogoče shraniti.'));
    } finally {
      setBusy(null);
    }
  };

  const download = async () => {
    setBusy('download');
    setError(null);
    try {
      const recovered = await recoverRecording(current.id);
      const stamp = recovered.startedAt.slice(0, 16).replace(/[:T]/g, '-');
      await saveFile(recovered.blob, `posnetek-${stamp}.${extensionForMime(recovered.mimeType)}`);
    } catch (err) {
      if (!isAbortError(err)) setError(errorText(err, 'Posnetka ni bilo mogoče prenesti.'));
    } finally {
      setBusy(null);
    }
  };

  const discard = async () => {
    const ok = await confirmDialog({
      title: 'Zavrzi posnetek?',
      message: 'Posnetek bo trajno izbrisan iz te naprave. Tega ni mogoče razveljaviti.',
      confirmLabel: 'Zavrzi',
      danger: true,
    });
    if (!ok) return;
    setBusy('discard');
    try {
      await discardRecording(current.id);
      done(current.id);
    } catch (err) {
      setError(errorText(err, 'Posnetka ni bilo mogoče izbrisati.'));
    } finally {
      setBusy(null);
    }
  };

  const later = () => {
    postponed.add(current.id);
    done(current.id);
  };

  const rejected = current.meta.rejected === true;
  const reason = str(current.meta.reason);

  return (
    <Modal
      title="Najden je neshranjen posnetek"
      onClose={busy ? undefined : later}
      actions={
        <>
          <button type="button" className="btn btn--ghost" onClick={later} disabled={busy !== null}>
            Pozneje
          </button>
          <button type="button" className="btn btn--danger-ghost" onClick={() => void discard()} disabled={busy !== null}>
            <Icon name="trash" size={18} /> Zavrzi
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={busy !== null} data-autofocus>
            <Icon name={busy === 'save' ? 'spinner' : 'check'} size={18} /> Shrani
          </button>
        </>
      }
    >
      <p>
        {rejected
          ? 'Strežnik tega posnetka ni sprejel, zato je ostal shranjen v tej napravi.'
          : 'Snemanje se ni pravilno zaključilo (npr. zaprta stran ali prazna baterija). Posneti del je ostal shranjen v tej napravi.'}
      </p>
      <dl className="facts">
        <dt>Vrsta</dt>
        <dd>{describe(current)}</dd>
        <dt>Začetek</dt>
        <dd>{formatDateTime(current.startedAt)}</dd>
        <dt>Trajanje</dt>
        <dd>
          {formatDuration(current.durationMs)} ({formatBytes(current.bytes)})
        </dd>
        {rejected && reason && (
          <>
            <dt>Razlog</dt>
            <dd>{reason}</dd>
          </>
        )}
      </dl>
      {items.length > 1 && <p className="muted small">Najdenih posnetkov: {items.length}</p>}
      <button type="button" className="btn btn--link" onClick={() => void download()} disabled={busy !== null}>
        <Icon name={busy === 'download' ? 'spinner' : 'download'} size={18} /> Prenesi posnetek v napravo
      </button>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}
